import AddIcon from "@mui/icons-material/AddRounded";
import { Avatar, Grid, Stack, Typography } from "@mui/material";
import { useCallback } from "react";
import { useNavigate } from "react-router";
import useRequest from "@entrilia/shared/hooks/useRequest";
import biApi from "../../../../../api/biApi";
import type { Dashboard } from "../../../../../api/types/dashboardsTypes";
import { WidgetType } from "../../../../../api/types/dashboardsTypes";
import type { Report } from "../../../../../api/types/svTypes";
import { useDashboardContext } from "../../contexts/DashboardContext";
import type { TileObjectSmartView } from "../../models/TileObject";
import { defaultSmartViewWidgetSize, generateWidgetId } from "./generateWidgetId";
import { reportItemSx } from "./reportItemSx";

const NewWidget = () => {
  const navigate = useNavigate();
  const { dashboard, addNewTile, createEditTileUrl } = useDashboardContext();
  const { request: createDraftReport } = useRequest(biApi.dashboardReports.createDraftReport);

  const onNewWidget = useCallback(async () => {
    const [report] = await createDraftReport();
    if (report && dashboard) {
      const newTile = createNewTile(report, dashboard);

      addNewTile(newTile);
      navigate(createEditTileUrl(newTile.id));
    }
  }, [createDraftReport, dashboard, addNewTile, navigate, createEditTileUrl]);

  return (
    <Grid sx={{ ...reportItemSx, cursor: "pointer" }}>
      <Avatar sx={{ width: 32, height: 32, backgroundColor: "action.hover", borderRadius: 1 }}>
        <AddIcon color="primary" />
      </Avatar>
      <Stack onClick={onNewWidget} direction="column" sx={{ cursor: "pointer" }}>
        <Typography variant="body1">New</Typography>
        <Typography variant="caption" color="textSecondary">
          Create from scratch. Tabular, Bar Chart etc.
        </Typography>
      </Stack>
    </Grid>
  );
};

export default NewWidget;

function createNewTile(report: Report, dashboard: Dashboard) {
  const id = generateWidgetId();
  const newTile: TileObjectSmartView = {
    id,
    isInitialized: true,
    isLoaded: false,
    item: {
      tileId: id,
      location: {
        row: 0,
        column: 0,
        width: defaultSmartViewWidgetSize.width,
        height: defaultSmartViewWidgetSize.height,
      },
      widget: {
        type: WidgetType.SmartView,
        reportId: report.reportId,
        widgetId: id,
        tileId: id,
        clientCode: report.clientCode,
        dashboardId: dashboard.dashboardId,
        nameUserInput: "New Widget",
        createdBy: "",
        emailCreatedBy: "",
        modifiedBy: "",
        emailModifiedBy: "",
        isDraft: true,
        etag: "",
      },
    },
  };

  return newTile;
}
